/**
 * GiftArticleService — mirrors ConneXt v2 ValidateGiftArticleLink + GenerateLink.
 * Handles gift article validation (bypass paywall) and link generation (share articles).
 */
import { HttpService } from '@Engage.Data/services/http/HttpService';
import { AppOptions } from '../../../application/options/AppOptions';
import { UserStore } from '../../auth/model/UserStore';
import { logService, eventsService, AppEvents } from '@Engage.Core';
import { getCurrentUrlWithoutParams, getUrlParam } from '@Engage.Core';
export class GiftArticleService {
    /**
     * Validates gift article URL params. If valid, bypasses paywall.
     * Mirrors ConneXt v2 ValidateGiftArticleLink.validateGiftArticleLink()
     */
    static async validate() {
        const giftParam = getUrlParam('ga') || getUrlParam('gfs');
        if (!giftParam)
            return false;
        const user = UserStore.getUser();
        // Don't validate if user is already subscribed
        if ((user === null || user === void 0 ? void 0 : user.UserState) === 'Subscribed')
            return false;
        logService.log('[GiftArticle] Gift article param detected, validating...');
        try {
            const senderUserId = (getUrlParam('gfs') || '').replace(/ /g, '+');
            const payload = {
                ReceiverUserId: (user === null || user === void 0 ? void 0 : user.CustomerRegistrationId) || (user === null || user === void 0 ? void 0 : user.MasterId) || '',
                Link: getCurrentUrlWithoutParams(),
                Hash: getUrlParam('h') || '',
                Expiry: getUrlParam('e') || '',
                SenderUserId: senderUserId,
                UserState: (user === null || user === void 0 ? void 0 : user.UserState) || 'LoggedOut',
                ConfigCode: AppOptions.getConfigCode(),
                GiftArticleParam: getUrlParam('ga') || ''
            };
            const response = await HttpService.post('gift/validatelink', payload);
            if ((response === null || response === void 0 ? void 0 : response.status) === true || (response === null || response === void 0 ? void 0 : response.Status) === true) {
                logService.log('[GiftArticle] Gift article validated — paywall bypassed');
                eventsService.dispatch(AppEvents.GiftArticleLinkValidated, {
                    articleLink: window.location.href,
                    giftorUserId: senderUserId,
                    gifteeUserId: payload.ReceiverUserId,
                    articleConsumedCount: response.giftLinkUsageCount || 0,
                    status: true
                });
                return true;
            }
            logService.log('[GiftArticle] Gift article validation failed');
            return false;
        }
        catch (err) {
            logService.warn('[GiftArticle] Validation error', err);
            return false;
        }
    }
    /**
     * Binds the generateLink button click (event delegation).
     * Mirrors ConneXt v2 GenerateLink mg2 action.
     */
    static bindGenerateLinkButtons() {
        document.addEventListener('click', (e) => {
            var _a;
            const target = (_a = e.target) === null || _a === void 0 ? void 0 : _a.closest('[data-mg2-action="generateLink"]');
            if (!target)
                return;
            e.preventDefault();
            e.stopPropagation(); // Prevent bubbling to giftArticleLink toggle
            this.handleGenerateLink(target);
        });
    }
    /** Alias for EngageFlow compatibility */
    static bindGiftArticleButtons() {
        this.bindGenerateLinkButtons();
        this.bindGiftArticleLinkToggle();
        this.applyGiftArticleVisibility();
    }
    static setSettings(settings) {
        this.giftArticleSettings = settings;
    }
    /**
     * Shows/hides giftArticleLink buttons based on config.
     * Mirrors ConneXt v2 GiftArticleLink.isShow() + shouldShowShareBox()
     */
    static applyGiftArticleVisibility() {
        var _a, _b, _c, _d;
        const isEnabled = ((_a = this.giftArticleSettings) === null || _a === void 0 ? void 0 : _a.EnableGiftArticle)
            || ((_b = this.giftArticleSettings) === null || _b === void 0 ? void 0 : _b.enableGiftArticle) || false;
        const isEnabledForNonSubscribed = ((_c = this.giftArticleSettings) === null || _c === void 0 ? void 0 : _c.EnableGiftArticleForNonSubscribed)
            || ((_d = this.giftArticleSettings) === null || _d === void 0 ? void 0 : _d.enableGiftArticleForNonSubscribed) || false;
        const user = UserStore.getUser();
        const userState = (user === null || user === void 0 ? void 0 : user.UserState) || 'LoggedOut';
        let shouldShow = false;
        if (isEnabledForNonSubscribed && isEnabled) {
            shouldShow = true;
        }
        else {
            shouldShow = userState === 'Subscribed' && isEnabled;
        }
        // Apply to all giftArticleLink buttons in the DOM
        document.querySelectorAll('[data-mg2-action="giftArticleLink"]').forEach(el => {
            el.style.display = shouldShow ? '' : 'none';
        });
    }
    /**
     * Binds the giftArticleLink button which creates and toggles the share box.
     * Mirrors ConneXt v2 GiftArticleLink mg2 action + toggleShareBox()
     */
    static bindGiftArticleLinkToggle() {
        document.addEventListener('click', (e) => {
            var _a, _b, _c;
            const target = (_a = e.target) === null || _a === void 0 ? void 0 : _a.closest('[data-mg2-action="giftArticleLink"]');
            if (!target)
                return;
            // Don't toggle if the click was on a generateLink button inside the share box
            const clickedGenerateLink = (_b = e.target) === null || _b === void 0 ? void 0 : _b.closest('[data-mg2-action="generateLink"]');
            if (clickedGenerateLink)
                return;
            // Don't toggle if click was inside the share box (copy button, input, etc.)
            const clickedInsideBox = (_c = e.target) === null || _c === void 0 ? void 0 : _c.closest('.engage-ga-box');
            if (clickedInsideBox)
                return;
            e.preventDefault();
            this.toggleShareBox(target);
        });
    }
    /**
     * Creates/toggles the share box inside the gift article button.
     * Mirrors ConneXt v2 toggleShareBox()
     */
    static toggleShareBox(button) {
        var _a, _b, _c, _d, _e, _f, _g;
        // Close any other open share boxes
        document.querySelectorAll('.engage-ga-box').forEach((box) => {
            if (!button.contains(box)) {
                box.remove();
            }
        });
        let shareBoxElement = button.querySelector('.engage-ga-box');
        if (!shareBoxElement) {
            // Get the correct template HTML based on user state
            const user = UserStore.getUser();
            const userState = (user === null || user === void 0 ? void 0 : user.UserState) || 'LoggedOut';
            const isNonSubscribedEnabled = ((_a = this.giftArticleSettings) === null || _a === void 0 ? void 0 : _a.EnableGiftArticleForNonSubscribed)
                || ((_b = this.giftArticleSettings) === null || _b === void 0 ? void 0 : _b.enableGiftArticleForNonSubscribed) || false;
            const subscribedHtml = ((_c = this.giftArticleSettings) === null || _c === void 0 ? void 0 : _c.SubscribedFormHtml)
                || ((_d = this.giftArticleSettings) === null || _d === void 0 ? void 0 : _d.subscribedFormHtml) || '';
            const nonSubscribedHtml = ((_e = this.giftArticleSettings) === null || _e === void 0 ? void 0 : _e.NonSubscribedFormHtml)
                || ((_f = this.giftArticleSettings) === null || _f === void 0 ? void 0 : _f.nonSubscribedFormHtml) || '';
            let shareBoxHtml = '';
            if (isNonSubscribedEnabled && userState !== 'Subscribed') {
                shareBoxHtml = nonSubscribedHtml || subscribedHtml || this.getDefaultShareBoxHtml();
            }
            else {
                shareBoxHtml = subscribedHtml || this.getDefaultShareBoxHtml();
            }
            if (!shareBoxHtml)
                return;
            // Create and append
            const tempDiv = document.createElement('div');
            tempDiv.innerHTML = shareBoxHtml;
            shareBoxElement = tempDiv.firstElementChild || tempDiv;
            button.appendChild(shareBoxElement);
            // Ensure button has relative position for absolute popup
            if (getComputedStyle(button).position === 'static') {
                button.style.position = 'relative';
            }
            // Hide login elements for logged-in users
            if (userState !== 'LoggedOut') {
                shareBoxElement.querySelectorAll("[data-mg2-action='login']")
                    .forEach(el => el.style.display = 'none');
            }
            // Close button inside popup
            (_g = shareBoxElement.querySelector('.closebtn')) === null || _g === void 0 ? void 0 : _g.addEventListener('click', (e) => {
                e.stopPropagation();
                shareBoxElement === null || shareBoxElement === void 0 ? void 0 : shareBoxElement.remove();
            });
        }
        else {
            // Toggle existing
            if (shareBoxElement.style.display === 'none') {
                shareBoxElement.style.display = 'block';
            }
            else {
                shareBoxElement.remove();
                return;
            }
        }
        shareBoxElement.style.display = 'block';
        // Position the popup (mirrors ConneXt v2 positionPopup)
        this.positionPopup(button, shareBoxElement);
        const resizeListener = () => this.positionPopup(button, shareBoxElement);
        window.addEventListener('resize', resizeListener);
    }
    /**
     * Positions the share box popup relative to the button with caret.
     * Mirrors ConneXt v2 positionPopup()
     */
    static positionPopup(button, popup) {
        // Remove previous caret classes
        ['caret-middle-top', 'caret-middle-bottom', 'caret-left-top', 'caret-left-bottom', 'caret-right-top', 'caret-right-bottom']
            .forEach(cls => popup.classList.remove(cls));
        const buttonRect = button.getBoundingClientRect();
        popup.style.position = 'absolute';
        popup.style.top = '';
        popup.style.left = '';
        popup.style.right = '';
        popup.style.bottom = '';
        const topPosition = buttonRect.bottom + window.scrollY;
        const popupWidth = popup.offsetWidth;
        const popupHeight = popup.offsetHeight;
        const viewportHeight = window.innerHeight;
        const btnWidth = button.offsetWidth;
        const btnHeight = button.offsetHeight;
        const halfButtonWidth = buttonRect.width / 2;
        const leftViewportWidth = buttonRect.left + halfButtonWidth;
        const rightViewportWidth = window.innerWidth - (buttonRect.right - halfButtonWidth);
        let caret = '';
        if (leftViewportWidth > popupWidth / 2 && rightViewportWidth > popupWidth / 2) {
            popup.style.left = `${-popupWidth / 2 + btnWidth / 2}px`;
            caret = 'caret-middle';
        }
        else if (buttonRect.right < popupWidth) {
            popup.style.left = '0px';
            caret = 'caret-left';
        }
        else {
            popup.style.right = '0px';
            caret = 'caret-right';
        }
        if (topPosition + popupHeight > viewportHeight + window.scrollY) {
            popup.style.bottom = `${btnHeight + 15}px`;
            caret = caret.concat('-bottom');
        }
        else {
            popup.style.top = `${btnHeight + 15}px`;
            caret = caret.concat('-top');
        }
        popup.classList.add(caret);
    }
    static getDefaultShareBoxHtml() {
        return `<div class="Mg2-connext engage-ga-box">
            <style>
                .engage-ga-box{padding:0 20px 20px;box-shadow:0 4px 9px rgba(23,26,31,0.19),0 0 2px rgba(23,26,31,0.2);text-align:center;position:absolute;z-index:1000;width:350px;background:#fff;border-radius:10px;border:3px solid #ddd;cursor:default;box-sizing:content-box;min-height:125px;}
                .engage-ga-box [data-mg2-action="generateLink"]{padding:15px 24px;color:#fff;background:#9095a1;border:none;border-radius:6px;font-size:14px;font-weight:bold;display:flex;align-items:center;justify-content:center;cursor:pointer;height:40px;position:absolute;bottom:0;width:100%;left:0;}
                .engage-ga-box [data-mg2-action="generateLink"]:hover{background:#6f7787;}
                .engage-ga-popup{display:flex;flex-direction:column;position:relative;min-height:125px;padding-bottom:50px;}
                .engage-ga-box .share-header{display:flex;justify-content:space-between;align-items:center;margin-top:18px;font-size:19px;font-weight:bold;color:#000;}
                .engage-ga-box .share-header span{font-size:20px;font-weight:bold;color:#333;}
                .engage-ga-box .closebtn{cursor:pointer;text-decoration:none;color:#333;}
            </style>
            <div class="engage-ga-popup">
                <div class="share-header">
                    <span>Share</span>
                    <a class="closebtn">×</a>
                </div>
                <button class="engage-ga-generate-link" data-mg2-action="generateLink" aria-label="Generate Link">
                    Generate Link
                </button>
            </div>
        </div>`;
    }
    /**
     * Handles the generate link flow with loader, copy link, error messages.
     * Mirrors ConneXt v2 GenerateLink.handleGenerateLink()
     */
    static async handleGenerateLink(button) {
        var _a, _b, _c, _d;
        const shareBox = button.closest('.engage-ga-box') || button.parentElement;
        // Show loader inside button (mirrors ConneXt v2 Loader class)
        // ConneXt v2: saves original HTML, adds 'disabled' class, appends loader img inside button
        const buttonSpan = button.querySelector('span');
        const targetElement = buttonSpan || button;
        const originalContent = targetElement.innerHTML;
        const loaderImg = document.createElement('img');
        loaderImg.src = 'data:image/gif;base64,R0lGODlhEAALAPQAAP///wAAANra2tDQ0Orq6gYGBgAAAC4uLoKCgmBgYLq6uiIiIkpKSoqKimRkZL6+viYmJgQEBE5OTubm5tjY2PT09Dg4ONzc3PLy8ra2tqCgoMrKyu7u7gAAAAAAAAAAACH/C05FVFNDQVBFMi4wAwEAAAAh/hpDcmVhdGVkIHdpdGggYWpheGxvYWQuaW5mbwAh+QQJCwAAACwAAAAAEAALAAAFLSAgjmRpnqSgCuLKAq5AEIM4zDVw03ve27ifDgfkEYe04kDIDC5zrtYKRa2WQgAh+QQJCwAAACwAAAAAEAALAAAFJGBhGAVgnqhpHIeRvsDawqns0qeN5+y967tYLyicBYE7EYkYAgAh+QQJCwAAACwAAAAAEAALAAAFNiAgjothLOOIJAkiGgxjpGKiKMkbz7SN6zIawJcDwIK9W/HISxGBzdHTuBNOmcJVCyoUlk7CEAAh+QQJCwAAACwAAAAAEAALAAAFNSAgjqQIRRFUAo3jNGIkSdHqPI8Tz3V55zuaDacDyIQ+YrBH+hWPzJFzOQQaeavWi7oqnVIhACH5BAkLAAAALAAAAAAQAAsAAAUyICCOZGme1rJY5kRRk7hI0mJSVUXJtF3iOl7tltsBZsNfUegjAY3I5sgFY55KqdX1GgIAIfkECQsAAAAsAAAAABAACwAABTcgII5kaZ4kcV2EqLJipmnZhWGXaOOitm2aXQ4g7P2Ct2ER4AMul00kj5g0Al8tADY2y6C+4FIIACH5BAkLAAAALAAAAAAQAAsAAAUvICCOZGme5ERRk6iy7qpyHCVStA3gNa/7txxwlwv2isSacYUc+l4tADQGQ1mvpBAAIfkECQsAAAAsAAAAABAACwAABS8gII5kaZ7kRFGTqLLuqnIcJVK0DeA1r/u3HHCXC/aKxJpxhRz6Xi0ANAZDWa+kEAA7AAAAAAAAAAAA';
        loaderImg.setAttribute('style', 'margin: 7px 7px; width: 16px; height: 11px;');
        loaderImg.id = 'engage-ga-loader';
        button.classList.add('disabled');
        targetElement.appendChild(loaderImg);
        // Remove previous errors
        (_a = shareBox === null || shareBox === void 0 ? void 0 : shareBox.querySelector('.engage-ga-error')) === null || _a === void 0 ? void 0 : _a.remove();
        (_b = shareBox === null || shareBox === void 0 ? void 0 : shareBox.querySelector('.engage-ga-validation')) === null || _b === void 0 ? void 0 : _b.remove();
        try {
            const user = UserStore.getUser();
            const payload = {
                UserId: (user === null || user === void 0 ? void 0 : user.CustomerRegistrationId) || (user === null || user === void 0 ? void 0 : user.MasterId) || '',
                Email: (user === null || user === void 0 ? void 0 : user.Email) || '',
                Link: getCurrentUrlWithoutParams(),
                ConfigCode: AppOptions.getConfigCode()
            };
            const response = await HttpService.get(`gift/generatelink?userId=${encodeURIComponent(payload.UserId)}&email=${encodeURIComponent(payload.Email)}&link=${encodeURIComponent(payload.Link)}&configCode=${encodeURIComponent(payload.ConfigCode)}`);
            // Remove loader — restore original content (mirrors ConneXt v2 Loader.off())
            button.classList.remove('disabled');
            targetElement.innerHTML = originalContent;
            if ((response === null || response === void 0 ? void 0 : response.status) === true || (response === null || response === void 0 ? void 0 : response.Status) === true) {
                const link = response.responseMessage || response.ResponseMessage || response.url || response.Url || '';
                // Dispatch event
                eventsService.dispatch(AppEvents.GiftArticleLinkGeneration, {
                    registrationId: payload.UserId,
                    articleLink: link,
                    expiryDate: response.expiryDate || response.ExpiryDate,
                    shareLimit: response.shareLimit || response.ShareLimit
                });
                // Hide the generate button (mirrors ConneXt v2: generateLinkDiv.style.display = 'none')
                button.style.display = 'none';
                // Create link box in button's parent (mirrors ConneXt v2: createLinkBox(generateLinkDivParent, link))
                const parent = button.parentElement;
                if (parent) {
                    this.createLinkBox(parent, link);
                }
            }
            else {
                // Show validation error before the button (mirrors ConneXt v2: insertBefore(ValidationDiv, validateBox))
                const msg = (response === null || response === void 0 ? void 0 : response.responseMessage) || (response === null || response === void 0 ? void 0 : response.ResponseMessage) || 'Unable to generate link.';
                const errorDiv = document.createElement('div');
                errorDiv.innerHTML = msg;
                errorDiv.className = 'engage-ga-validation';
                errorDiv.style.cssText = 'color:#c0392b;font-size:12px;margin-top:8px;';
                (_c = button.parentNode) === null || _c === void 0 ? void 0 : _c.insertBefore(errorDiv, button);
            }
        }
        catch (error) {
            // Remove loader — restore original content (mirrors ConneXt v2 Loader.off())
            button.classList.remove('disabled');
            targetElement.innerHTML = originalContent;
            // Show error before the button (mirrors ConneXt v2)
            const errorDiv = document.createElement('div');
            errorDiv.innerHTML = 'Sorry, we\u2019re unable to generate the gift article link right now. Please try again later.';
            errorDiv.className = 'engage-ga-error';
            errorDiv.style.cssText = 'color:#c0392b;font-size:12px;margin-top:8px;';
            (_d = button.parentNode) === null || _d === void 0 ? void 0 : _d.insertBefore(errorDiv, button);
            logService.error('[GiftArticle] Generate link failed', error);
        }
    }
    /**
     * Creates the link box with input + copy button.
     * Mirrors ConneXt v2 createLinkBox()
     */
    static createLinkBox(parentElement, link) {
        const linkBoxHtml = `<div class="engage-ga-link-box">
            <input type="text" value="${link}" readonly style="width:calc(100% - 45px);padding:8px;border:1px solid #ddd;border-radius:4px 0 0 4px;font-size:12px;box-sizing:border-box;">
            <button class="engage-ga-copy-button" aria-label="Copy" title="Copy link" style="padding:8px 12px;background:#2f3544;color:#fff;border:none;border-radius:0 4px 4px 0;cursor:pointer;font-size:12px;">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path fill-rule="evenodd" clip-rule="evenodd" d="M7 5C7 3.34315 8.34315 2 10 2H19C20.6569 2 22 3.34315 22 5V14C22 15.6569 20.6569 17 19 17H17V19C17 20.6569 15.6569 22 14 22H5C3.34315 22 2 20.6569 2 19V10C2 8.34315 3.34315 7 5 7H7V5ZM9 7H14C15.6569 7 17 8.34315 17 10V15H19C19.5523 15 20 14.5523 20 14V5C20 4.44772 19.5523 4 19 4H10C9.44772 4 9 4.44772 9 5V7ZM5 9C4.44772 9 4 9.44772 4 10V19C4 19.5523 4.44772 20 5 20H14C14.5523 20 15 19.5523 15 19V10C15 9.44772 14.5523 9 14 9H5Z" fill="currentColor"></path>
                </svg>
            </button>
            <button class="engage-ga-copied-button" aria-label="Copied" title="Copied!" style="display:none;padding:8px 12px;background:#27ae60;color:#fff;border:none;border-radius:0 4px 4px 0;cursor:pointer;font-size:12px;">
                <span>&#10003;</span>
            </button>
        </div>`;
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = linkBoxHtml;
        const linkBoxElement = tempDiv.firstElementChild;
        parentElement.appendChild(linkBoxElement);
        // Attach copy functionality
        const copyButton = linkBoxElement.querySelector('.engage-ga-copy-button');
        const copiedButton = linkBoxElement.querySelector('.engage-ga-copied-button');
        const inputField = linkBoxElement.querySelector('input');
        copyButton === null || copyButton === void 0 ? void 0 : copyButton.addEventListener('click', () => {
            var _a;
            inputField.select();
            (_a = navigator.clipboard) === null || _a === void 0 ? void 0 : _a.writeText(inputField.value).catch(() => {
                document.execCommand('copy');
            });
            // Toggle button visibility
            copyButton.style.display = 'none';
            copiedButton.style.display = 'inline-block';
            setTimeout(() => {
                copyButton.style.display = 'inline-block';
                copiedButton.style.display = 'none';
            }, 2000);
        });
    }
}
/** Store gift article settings from API response */
GiftArticleService.giftArticleSettings = null;
